import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { Course } from '../types';
import { BookOpen, Clock, ChevronRight, Loader2 } from 'lucide-react';

export default function Courses() {
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadCourses();
  }, []);

  const loadCourses = async () => {
    try {
      const data = await api.getCourses();
      setCourses(data);
    } catch (error) {
      console.error('Failed to load courses:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 text-apple-blue animate-spin" />
          <p className="text-apple-text-secondary">Loading courses...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-apple-text mb-2">Available Courses</h1>
        <p className="text-apple-text-secondary">
          Choose a course to start your learning journey
        </p>
      </div>

      {courses.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 bg-gray-100 rounded-apple-sm flex items-center justify-center mx-auto mb-4">
            <BookOpen className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium text-apple-text mb-2">No courses available yet</h3>
          <p className="text-apple-text-secondary">
            Check back later for new courses!
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {courses.map((course) => (
            <Link
              key={course.id}
              to={`/learn/${course.id}`}
              className="card group hover:shadow-apple-hover transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-apple-blue to-apple-purple rounded-apple-sm flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-white" />
                </div>
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Available
                </span>
              </div>

              <h2 className="text-xl font-semibold text-apple-text mb-2 group-hover:text-apple-blue transition-colors">
                {course.title}
              </h2>
              
              <p className="text-apple-text-secondary text-sm mb-4 line-clamp-2">
                {course.description || 'Start learning Data Science with this comprehensive course.'}
              </p>

              <div className="flex items-center justify-between pt-4 border-t border-apple-border">
                <div className="flex items-center gap-2 text-sm text-apple-text-secondary">
                  <Clock className="w-4 h-4" />
                  <span>Self-paced</span>
                </div>
                <div className="flex items-center gap-1 text-apple-blue font-medium text-sm">
                  Start Learning
                  <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
